import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from 'apps/invoice2go/src/environments/environment';
import { EnvironmentsEnum } from 'apps/invoice2go/src/environments/environments.enum';
import { extend, isNumber } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { AuthHttpService } from '../auth-data/auth-http.service';
import { ErrorService } from '../error-reporting/error.service';
import { IResponseInfo } from '../http/http.data';
import { ImageService } from '../shared/services/image/image.service';
import { UrlService } from '../shared/services/url/url.service';
import { ACCOUNT_COUNTRY_HEADER } from './geolocation-data.data';
import { LocationInfo } from './models/location-info.model';
import { LocationSuggestions } from './models/location-suggestions.model';

@Injectable()
export class GeolocationDataService {
  private _locationInfoReq: Observable<LocationInfo>;
  private _locationInfo: LocationInfo;

  constructor(
    private _authHttpService: AuthHttpService,
    private _urlService: UrlService,
    private _errorService: ErrorService,
    private _imageService: ImageService
  ) {}

  getLocationSuggestions(
    location: string,
    locationInfo: LocationInfo,
    accountCountry: string
  ): Observable<LocationSuggestions> {
    if (location && locationInfo) {
      const params = { input: location };
      const loc = locationInfo.location;
      const lat = loc.latitude;
      const long = loc.longitude;
      const countryCode = locationInfo.country.isoCode;
      const useLatLong = loc.accuracyRadius < 100000;

      if (useLatLong && isNumber(lat) && isNumber(long)) {
        extend(params, { latitude: lat, longitude: long });
      }

      if (countryCode) {
        extend(params, { country: countryCode });
      }

      return this._authHttpService
        .get('/location/suggestions', params, {
          [ACCOUNT_COUNTRY_HEADER]: accountCountry,
        })
        .pipe(
          map(
            ({ body }: IResponseInfo) =>
              new LocationSuggestions(body['suggestions'])
          )
        );
    } else {
      return of(new LocationSuggestions());
    }
  }

  getLocationInfo(): Observable<LocationInfo> {
    // disable fetching location info on local dev
    if (environment.env === EnvironmentsEnum.localdev) {
      this._locationInfo = new LocationInfo();
      return of(null);
    }

    if (!this._locationInfo) {
      if (!this._locationInfoReq) {
        this._locationInfoReq = this._authHttpService
          .get('/location/geoip')
          .pipe(
            map(({ body }: IResponseInfo) => {
              this._locationInfo = new LocationInfo(body);

              return this._locationInfo;
            }),
            catchError((error) => {
              this._locationInfo = new LocationInfo();

              this._errorService.alertSystem(
                new Error('location info error'),
                error
              );

              return of(null);
            }),
            finalize(() => (this._locationInfoReq = null))
          );
      }

      return this._locationInfoReq;
    } else {
      return of(this._locationInfo);
    }
  }

  getMapImageUrl(
    latitude: number,
    longitude: number,
    zoom: number,
    height: number,
    width: number
  ): Observable<Blob> {
    const baseUrl = '/location/maptile';
    const params: any = this._urlService.serializeParams({
      latitude,
      longitude,
      zoom,
      height,
      width,
    });

    return this._imageService.getImageAsBlob(
      `${baseUrl}${Location.normalizeQueryParams(params)}`,
      true
    );
  }
}
